import { useEffect, useState } from 'react';
import { useDataService } from '../services/ApiDataService';
import { getErrMessage, to } from './PromiseUtilities';
import useAlerts from '../stores/AlertsStore';

/**
 * Fetches data from API endpoint on mount with automatic error handling
 *
 * @template T
 * @param {Object} config
 * @param {string} config.endpoint API endpoint path
 * @param {Object} [config.params] Query parameters
 * @param {(data: any) => T} [config.selector] Extract specific data from response (e.g., data => data?.sites)
 * @param {T} [config.initialState] Initial state value
 * @param {any[]} [config.dependencies] Additional dependencies for refetch
 * @returns {T} The fetched data
 *
 * @example
 * const sites = useApiDataFetch({
 *   endpoint: `/ngp/v2/devices/${deviceId}`,
 *   selector: (data) => data?.sites,
 *   initialState: []
 * });
 */
export default function useApiDataFetch({ endpoint, params, selector, initialState = null, dependencies = [] }) {
    const [data, setData] = useState(initialState);
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();

    useEffect(() => {
        (async () => {
            const [err, resultData] = await to(dataService.get(endpoint, params));
            if (err) {
                alertsActions.createErrorAlert(getErrMessage(err));
                return;
            }
            const extractedData = selector ? selector(resultData) : resultData;
            setData(extractedData);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);

    return data;
}
